import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	IDatasource,
	IGetRowsParams,
	SortModelItem,
} from "ag-grid-community";
import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

import { CobblesReportToolbar } from "./CobblesReportToolbar";
import { CobblesSummaryBand } from "./CobblesSummaryBand";
import { useCobblesPickers } from "../hooks/useCobblesReport";
import { getCobblesData } from "../repositories/CobblesReportRepository";
import type {
	CobbleRow,
	CobblesSortType,
	CobblesSummary,
} from "../models/CobblesReport";

const BLOCK_SIZE = 100;

const SORTABLE_FIELD_TO_KEY: Record<string, string> = {
	StartString: "START",
	CobbleStandName: "COBBLESTANDNAME",
	ProductName: "PRODUCTNAME",
	CrewId: "CREWID",
	DurationString: "DURATION",
	BilletsCountBeforeCobble: "BILLETSCOUNTBEFORECOBBLE",
};

const mapSortModel = (
	sortModel: SortModelItem[] | undefined,
): CobblesSortType | null => {
	if (!sortModel || sortModel.length === 0) return null;
	const first = sortModel[0];
	const key = SORTABLE_FIELD_TO_KEY[first.colId];
	if (!key) return null;
	const dir = first.sort === "asc" ? "ASC" : "DESC";
	return `${key}-${dir}` as CobblesSortType;
};

interface CobblesFilters {
	crewId: string | null;
	productSizeId: number | null;
	productTypeId: number | null;
}

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const buildColumnDefs = (): ColDef<CobbleRow>[] => [
	{
		field: "StartString",
		headerName: "Timestamp",
		width: 170,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) =>
			dxtToLocalServerTime(p.data?.Start, "yyyy-MM-dd HH:mm:ss"),
	},
	{
		field: "CobbleStandName",
		headerName: "Stand",
		width: 100,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ProductName",
		headerName: "Product",
		flex: 1,
		minWidth: 200,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CrewId",
		headerName: "Shift",
		width: 80,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "DurationString",
		headerName: "Time to start again",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "BilletsCountBeforeCobble",
		headerName: "Billet # in campaign",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	crewId: urlParam.string(),
	productSizeId: urlParam.int(),
	productTypeId: urlParam.int(),
};

export const CobblesReport = () => {
	// Nothing is validated away here: the pickers list only what happened inside the range,
	// so dropping a crew or a product because the new range does not contain it would erase
	// a filter the operator just set.
	const [{ start, end, crewId, productSizeId, productTypeId }, setUrlState] =
		useUrlState(URL_SCHEMA);
	const filters: CobblesFilters = useMemo(
		() => ({ crewId, productSizeId, productTypeId }),
		[crewId, productSizeId, productTypeId],
	);
	const [summary, setSummary] = useState<CobblesSummary | null>(null);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<CobbleRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const pickersQuery = useCobblesPickers({ start: startStr, end: endStr });
	const columnDefs = useMemo(() => buildColumnDefs(), []);

	const buildDatasource = useCallback(
		(snapshot: {
			start: string;
			end: string;
			filters: CobblesFilters;
		}): IDatasource => ({
			getRows: (params: IGetRowsParams) => {
				const sortType = mapSortModel(params.sortModel);
				getCobblesData({
					start: snapshot.start,
					end: snapshot.end,
					crewId: snapshot.filters.crewId,
					productSizeId: snapshot.filters.productSizeId,
					productTypeId: snapshot.filters.productTypeId,
					fromRow: params.startRow,
					rowsToFetch: params.endRow - params.startRow,
					sortType,
				})
					.then(({ rows, summary: sum }) => {
						setSummary(sum);
						const lastRow =
							rows.length < params.endRow - params.startRow
								? params.startRow + rows.length
								: -1;
						params.successCallback(rows, lastRow);
					})
					.catch((err: Error) => {
						setErrorMsg(err.message);
						params.failCallback();
					});
			},
		}),
		[],
	);

	const refreshDatasource = useCallback(
		(args?: { start?: string; end?: string; filters?: CobblesFilters }) => {
			if (!gridApiRef.current) return;
			const ds = buildDatasource({
				start: args?.start ?? startStr,
				end: args?.end ?? endStr,
				filters: args?.filters ?? filters,
			});
			gridApiRef.current.setGridOption("datasource", ds);
		},
		[buildDatasource, startStr, endStr, filters],
	);

	const handleGridReady = useCallback(
		(event: GridReadyEvent<CobbleRow>) => {
			gridApiRef.current = event.api;
			event.api.setGridOption(
				"datasource",
				buildDatasource({ start: startStr, end: endStr, filters }),
			);
		},
		// The values at grid-ready time are the ones the URL arrived with; every later change
		// is pushed by the effect below.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// The grid holds an infinite datasource bound once, so it does not follow a render. Any
	// change of the URL has to rebind it: a filter, Back, or a link pasted into the bar.
	useEffect(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.setGridOption(
			"datasource",
			buildDatasource({ start: startStr, end: endStr, filters }),
		);
	}, [startStr, endStr, filters, buildDatasource]);

	const handleStartChange = useCallback(
		(d: moment.Moment | null) => {
			if (isUsableDate(d) && d) setUrlState({ start: d });
		},
		[setUrlState],
	);

	const handleEndChange = useCallback(
		(d: moment.Moment | null) => {
			if (isUsableDate(d) && d) setUrlState({ end: d });
		},
		[setUrlState],
	);

	const updateFilters = useCallback(
		(patch: Partial<CobblesFilters>) => setUrlState(patch),
		[setUrlState],
	);

	const handleRefresh = useCallback(() => {
		pickersQuery.refetch();
		refreshDatasource();
	}, [pickersQuery, refreshDatasource]);

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: "Cobbles_Report.xlsx",
			columnKeys: [
				"StartString",
				"CobbleStandName",
				"ProductName",
				"CrewId",
				"DurationString",
				"BilletsCountBeforeCobble",
			],
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "StartString") {
					const data = params.node?.data as CobbleRow | undefined;
					return dxtToLocalServerTime(data?.Start, "yyyy-MM-dd HH:mm:ss");
				}
				return params.value ?? "";
			},
		});
	}, []);

	const isLoading = pickersQuery.isFetching;

	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Cobbles Report
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<CobblesReportToolbar
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					productTypeId={filters.productTypeId}
					productSizeId={filters.productSizeId}
					crewId={filters.crewId}
					onProductTypeChange={(id) =>
						updateFilters({ productTypeId: id, productSizeId: null })
					}
					onProductSizeChange={(id) => updateFilters({ productSizeId: id })}
					onCrewChange={(id) => updateFilters({ crewId: id })}
					productTypes={pickersQuery.data?.productTypes ?? []}
					productSizes={pickersQuery.data?.productSizes ?? []}
					crews={pickersQuery.data?.crews ?? []}
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={isLoading}
				/>
			</Paper>

			<CobblesSummaryBand summary={summary} />

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				<AgGridReact<CobbleRow>
					columnDefs={columnDefs}
					rowModelType="infinite"
					cacheBlockSize={BLOCK_SIZE}
					maxBlocksInCache={10}
					infiniteInitialRowCount={1}
					defaultColDef={{
						resizable: true,
					}}
					suppressContextMenu
					headerHeight={32}
					rowHeight={30}
					onGridReady={handleGridReady}
				/>
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
